import { createClient } from '@supabase/supabase-js';
import { SPAM_TABLES, isSpamTable, spamMarkPatch, notSpamPatch, isMissingSpamColumn } from './lib/spamMark';
import { probeHandledColumns, markLeadsHandled, unmarkLeadsHandled } from './lib/leadHandled';
import { probeAlertNoteColumn } from './lib/systemAlerts';
import { loadDrafts } from './lib/drafts';
import { cleanNotifications } from './lib/voicemailAlert';
import { withTestAddress } from './lib/testMark';   // DAY-DIRECTSTAFF-2026-11-09 §1

const supabaseUrl = 'https://loqnsjyyyctbqcaoomce.supabase.co';
const supabaseKey = 'sb_publishable_p3-Dl9-STP2XCRFFd0Ekjw_O0uQEn_5';

export const supabase = createClient(supabaseUrl, supabaseKey);

export const db = {
  // ── CANDIDATES ─────────────────────────────────────────────
  async getCandidates() {
    const { data, error } = await supabase.from('candidates').select('*').order('updated_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((r) => withTestAddress(r));   // 11-09 §1: test data by its address reads as marked (nothing written)
  },
  async updateCandidate(id, updates) {
    const { data, error } = await supabase.from('candidates').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id).select().single();
    if (error) throw error;
    return data;
  },
  async addCandidate(candidate) {
    const { data, error } = await supabase.from('candidates').insert(candidate).select().single();
    if (error) throw error;
    return data;
  },

  // ── ACTIVITY LOG ───────────────────────────────────────────
  async getActivity(candidateId) {
    const { data, error } = await supabase.from('activity_log').select('*').eq('candidate_id', candidateId).order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },
  async getAllActivity() {
    const { data, error } = await supabase.from('activity_log').select('*').order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },
  async addActivity(candidateId, type, note) {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase.from('activity_log').insert({
      candidate_id: candidateId,
      date: today,
      type,
      note
    }).select().single();
    if (error) throw error;
    await supabase.from('candidates').update({ last_contact: today, updated_at: new Date().toISOString() }).eq('id', candidateId);
    return data;
  },

  // ── EMAIL SYNC ENTRIES ─────────────────────────────────────
  // These are stored as activity_log rows with type='email_sync'
  // The note field contains a JSON string with email data
  async getEmailSync(candidateId) {
    const { data, error } = await supabase
      .from('activity_log')
      .select('*')
      .eq('candidate_id', candidateId)
      .eq('type', 'email_sync')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(row => {
      try {
        const parsed = JSON.parse(row.note);
        return { ...row, emailData: parsed };
      } catch {
        return { ...row, emailData: { subject: 'Email', snippet: row.note, date: row.date, from: 'unknown', direction: 'inbound' } };
      }
    });
  },
  async getAllEmailSync() {
    const { data, error } = await supabase
      .from('activity_log')
      .select('*')
      .eq('type', 'email_sync')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  // ── MESSAGES ───────────────────────────────────────────────
  async getAllMessages() {
    const { data, error } = await supabase.from('messages').select('*').order('time', { ascending: true });
    if (error) throw error;
    return data;
  },
  async addMessage(candidateId, fromType, text, mode) {
    const { data, error } = await supabase.from('messages').insert({
      candidate_id: candidateId,
      from_type: fromType,
      text,
      mode,
      read: fromType === 'recruiter'
    }).select().single();
    if (error) throw error;
    return data;
  },
  async markMessagesRead(candidateId) {
    await supabase.from('messages').update({ read: true }).eq('candidate_id', candidateId).eq('from_type', 'candidate');
  },

  // ── SETTINGS ───────────────────────────────────────────────
  async getSettings() {
    const { data, error } = await supabase.from('settings').select('*');
    if (error) throw error;
    const obj = {};
    (data || []).forEach(row => { obj[row.key] = row.value; });
    return obj;
  },
  async saveSetting(key, value) {
    await supabase.from('settings').upsert({ key, value }, { onConflict: 'key' });
  },
  // The Text button's templates (CRM-TEXT-2026-10-16): read one key, and a save that THROWS on failure, so the
  // compose box can say "not saved" instead of quietly keeping the old ones.
  async getSetting(key) {
    const { data, error } = await supabase.from('settings').select('value').eq('key', key).maybeSingle();
    if (error) throw error;
    return data ? data.value : null;
  },
  async saveSettingChecked(key, value) {
    const { error } = await supabase.from('settings').upsert({ key, value }, { onConflict: 'key' });
    if (error) throw error;
  },

  // ── EMAIL TEMPLATES ──────────────────────────────────────────────────────────
  // SQL to run in Supabase:
  // CREATE TABLE IF NOT EXISTS email_templates (
  //   id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  //   label text NOT NULL,
  //   icon text DEFAULT '📧',
  //   subject text DEFAULT '',
  //   body text DEFAULT '',
  //   stage text DEFAULT '',
  //   sort_order integer DEFAULT 0,
  //   created_at timestamptz DEFAULT now(),
  //   updated_at timestamptz DEFAULT now()
  // );
  async getTemplates() {
    const { data, error } = await supabase.from('email_templates').select('*').order('sort_order').order('created_at');
    if (error) throw error;
    return data || [];
  },
  async addTemplate(tpl) {
    const { data, error } = await supabase.from('email_templates').insert(tpl).select().single();
    if (error) throw error;
    return data;
  },
  async updateTemplate(id, updates) {
    const { data, error } = await supabase.from('email_templates').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id).select().single();
    if (error) throw error;
    return data;
  },
  async deleteTemplate(id) {
    const { error } = await supabase.from('email_templates').delete().eq('id', id);
    if (error) throw error;
  },

  // ── DELETE CANDIDATE ─────────────────────────────────────────────────────────
  async deleteCandidate(id) {
    await supabase.from('activity_log').delete().eq('candidate_id', id);
    const { error } = await supabase.from('candidates').delete().eq('id', id);
    if (error) throw error;
  },

  // ── LEADS (public.leads — site forms + agent contact captures) ───────────────
  // Read-MOSTLY in the CRM. The writes are spam_marked_at and, since 2026-10-01,
  // processed / processed_at / processed_note ("Mark handled", below); every other
  // column is the leads worker's. RLS: requires an `authenticated` SELECT policy on
  // public.leads, and now an `authenticated` UPDATE policy for the spam mark to
  // land — see setLeadSpam(). anon is intentionally blocked for both.
  async getLeads() {
    const { data, error } = await supabase.from('leads').select('*').order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((r) => withTestAddress(r));   // 11-09 §1: test data by its address reads as marked (nothing written)
  },

  // ── AGENT CONVERSATIONS / MESSAGES / NOTIFICATIONS (recruiter-agent) ──────────
  async getAgentConversations() {
    const { data, error } = await supabase.from('agent_conversations').select('*').order('started_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },
  // Lightweight refs for per-conversation message counts, last activity AND who
  // is waiting (see `role`, below).
  //
  // created_at is selected as well as conversation_id, and it is not decoration:
  // agent_conversations.last_message_at is written by nothing (no PATCH in the
  // worker, no trigger in any migration), so it is frozen at row creation and is
  // a synonym for started_at. The only true "last activity" is
  // max(agent_messages.created_at), folded client-side in
  // src/lib/conversationView.js. See the 2026-09-08 chat audit, section 4.
  //
  // Still two narrow columns, so this stays a cheap query rather than pulling
  // message bodies into the list view.
  async getAgentMessageRefs() {
    // `role` added 2026-09-18 (ESCALATION-LOOP-SPEC E). conversationTriage.waitingOn
    // decides who is waiting from the LAST message's role, and the column was simply
    // absent from this select -- so the function was written, tested, and unwirable.
    // Still three narrow columns; no message body is pulled into the list view.
    const { data, error } = await supabase.from('agent_messages').select('conversation_id, created_at, role');
    if (error) throw error;
    return data || [];
  },
  // Full threaded transcript for one conversation (oldest-first).
  // NEEDS-HUMAN-FIX-2026-10-16: the newest inbound message of each listed conversation (the row's "last reply" text)
  async getLastInbound(conversationIds) {
    const ids = [...new Set(conversationIds || [])].filter(Boolean);
    if (!ids.length) return new Map();
    const { data, error } = await supabase.from('agent_messages').select('conversation_id,body,created_at,role')
      .in('conversation_id', ids).in('role', ['candidate', 'user']).order('created_at', { ascending: false }).limit(2000);
    if (error) throw error;
    const m = new Map();
    for (const r of data || []) if (!m.has(r.conversation_id)) m.set(r.conversation_id, r);
    return m;
  },
  async getConversationMessages(conversationId) {
    const { data, error } = await supabase
      .from('agent_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },
  // ── AGENT LEAD DECISIONS (read-only) ─────────────────────────────────────────
  // One row per first-contact CONSIDERATION, written by the worker's /lead-webhook
  // and by the */15 sweeper. The CRM only ever reads it. RLS: authenticated = ALL,
  // same posture as agent_notifications. If the 2026-09-02 migration has not been
  // run in this project the select 404s, which the caller treats as "no log yet"
  // rather than an error — the CRM must not break over a table it only observes.
  async getLeadDecisions() {
    const { data, error } = await supabase
      .from('agent_lead_decisions')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },
  async getAgentNotifications() {
    const { data, error } = await supabase.from('agent_notifications').select('*').order('created_at', { ascending: false });
    if (error) throw error;
    // FIX-AGENTS-2026-10-12 §7: an old voicemail row's Twilio URL never reaches state or a view (lib/voicemailAlert).
    return cleanNotifications(data || []);
  },

  // ── SPAM MARK (public.leads / public.chat_leads) ─────────────────────────────
  //
  // The column is defined by pt-agents/db/2026-09-12-spam-marked-at.sql on branch
  // gated/welcome-emails. It is applied BY HAND and had not been applied when this
  // shipped, so every path here is written for the column being absent.
  //
  // PROBE ONCE, NOT PER ROW. A table full of leads must not become a table full of
  // HEAD requests. The result is memoised for the life of the page — the column
  // cannot appear mid-session without a migration being run underneath a logged-in
  // recruiter, and if that happens a reload is the fix.
  //
  // The probe asks for the column and no rows (`limit(0)`): PostgREST still has to
  // resolve the column name to answer, so an unknown column errors, and zero rows
  // cross the wire either way. `head: true` on top of that means no body at all.
  _spamProbe: null,
  async spamColumnPresent() {
    if (this._spamProbe) return this._spamProbe;
    this._spamProbe = (async () => {
      const out = {};
      for (const table of SPAM_TABLES) {
        try {
          const { error } = await supabase.from(table)
            .select('spam_marked_at', { head: true, count: undefined })
            .limit(0);
          // A missing COLUMN means the feature is off for this table. A missing
          // TABLE, or an RLS denial, is a different fact and also means "cannot
          // use it here" -- so anything that errors reads as absent. The action is
          // hidden either way, which is the safe direction: a recruiter never sees
          // a button that cannot work.
          out[table] = !error;
        } catch (_) {
          out[table] = false;
        }
      }
      return out;
    })();
    return this._spamProbe;
  },

  // Mark (or unmark) one row. Returns the patch that was applied, so the caller can
  // fold it into the list it is already rendering without a refetch.
  //
  // Throws on failure -- INCLUDING a missing column. The UI hides the action when
  // the probe says the column is absent, so reaching here with no column means the
  // probe and the write disagree, and swallowing that would leave a recruiter
  // believing a row was marked when nothing was written. Fail loudly.
  async setLeadSpam(table, id, marked, row = null) {
    if (!isSpamTable(table)) throw new Error(`setLeadSpam: ${table} is not a spam-markable table`);
    // "Not spam" (FIX-AGENTS-2026-10-12 §4) also clears a duplicate link; a mark stays spamMarkPatch(true).
    const patch = marked ? spamMarkPatch(true) : notSpamPatch(row);
    const { error } = await supabase.from(table).update(patch).eq('id', id);
    if (error) {
      if (isMissingSpamColumn(error)) {
        throw new Error(
          `${table}.spam_marked_at does not exist — run pt-agents/db/2026-09-12-spam-marked-at.sql`);
      }
      throw error;
    }
    return patch;
  },

  // ── MARK HANDLED (public.leads.processed / processed_at / processed_note) ─────
  // All logic lives in ./lib/leadHandled (unit-tested against a fake client). These
  // bind it to the real client and to the once-per-session column probe:
  // processed_at / processed_note are added by
  // migrations/2026-10-01-leads-processed-note.sql, applied by hand, and until then
  // the write sends { processed } only (see leadHandled.js for the full contract).
  _handledProbe: null,
  async handledColumnsPresent() {
    if (!this._handledProbe) this._handledProbe = probeHandledColumns(supabase);
    return this._handledProbe;
  },
  // System alerts bulk close (NIGHT-AGENTS-2026-10-16 §2): is agent_notifications.handled_note there yet?
  async alertNoteColumnPresent() {
    if (!this._alertNoteProbe) this._alertNoteProbe = probeAlertNoteColumn(supabase);
    return this._alertNoteProbe;
  },
  // Second-touch drafts (SECOND-TOUCH-2026-10-02). Writes go through lib/drafts.js.
  async getDrafts() {
    return loadDrafts(supabase);
  },
  async markLeadsHandled(ids, note) {
    return markLeadsHandled(supabase, ids, note, { noteColumns: await this.handledColumnsPresent() });
  },
  async unmarkLeadsHandled(ids) {
    return unmarkLeadsHandled(supabase, ids, { noteColumns: await this.handledColumnsPresent() });
  },
};

// ─── STAFF REPLY (recruiter-agent, via the same-origin Pages Function proxy) ─────
// These do NOT talk to Supabase or to the Worker directly. The Worker's staff routes
// are authenticated by STAFF_REPLY_SECRET, which must never be in this bundle — so the
// browser posts same-origin to /api/staff-*, and the Pages Function attaches the secret.
// We forward the Supabase session token so the Function can prove a real CRM user is
// behind the request (see functions/api/_staff.js).

const STAFF_ERRORS = {
  unauthorized: 'Not signed in, or your session expired.',
  forbidden: 'Blocked: this request did not come from the CRM.',
  not_configured: 'Staff replies are not configured yet (missing Pages secret).',
  conversation_not_found: 'That conversation no longer exists.',
  opted_out: 'This candidate replied STOP. No message was sent.',
  not_an_sms_conversation: 'Staff replies only work on SMS threads.',
  send_failed: 'Twilio rejected the message. Nothing was delivered.',
  upstream: 'Could not reach the agent worker.',
  not_found: 'The agent worker does not have this action yet (its /staff-note route is not deployed). Nothing was changed.',
  note_not_recorded: 'The note could not be saved (the worker or its database is not ready for it yet). Nothing was changed.',
  store_unavailable: 'The agent database is unreachable. Nothing was changed; try again shortly.',
  invalid_number: 'This lead has no textable US mobile number on file. Nothing was sent.',
  quiet_hours: "It is outside 8am-8pm in this person's local time. Tick \"Send anyway\" to send now.",
  rate_limited: 'Ten texts a minute is the limit. Try again shortly.',
  lead_not_found: 'That lead no longer exists.',
  candidate_not_found: 'That candidate no longer exists. Nothing was sent.',
  // EXAMS-SEND-2026-10-16: the Send dialog's company email (crm-mailer)
  email_not_ready: "The company email isn't set up yet (the DNS is pending). Use your mail app for now.",
  bad_address: 'That email address does not look right. Nothing was sent.',
  no_portal_link: 'The message must keep the link. Nothing was sent.',
  recipient_suppressed: 'This address bounced or complained before, so the company email will not send to it.',
  body_too_long: 'The message is too long.',
};

/**
 * The Play button (FIX-AGENTS-2026-10-12 §7): the voicemail's audio as a Blob, from the same-origin Pages Function with
 * the signed-in session. Never a Twilio URL. A failure throws an Error whose .code is the proxy's / Worker's code.
 */
export async function voicemailAudio(alertId) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session && session.access_token;
  if (!token) { const e = new Error('session'); e.code = 'unauthorized'; throw e; }
  const res = await fetch('/api/voicemail-audio', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ alert_id: alertId }),
  });
  if (!res.ok || !String(res.headers.get('content-type') || '').startsWith('audio/')) {
    const data = await res.json().catch(() => ({}));
    const e = new Error(data.error || 'play_failed');
    e.code = data.error || 'unknown';
    throw e;
  }
  return res.blob();
}

async function postStaff(path, payload) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session && session.access_token;
  if (!token) throw new Error('Your session expired — sign in again to send.');

  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // The proxy passes the Worker's real reason through — surface it, don't flatten it.
    const err = new Error(data.message || STAFF_ERRORS[data.error] || 'Send failed. Try again.');
    err.code = data.error || 'unknown';
    throw err;
  }
  return data;
}

export const staff = {
  /** Send a recruiter-typed SMS. Takes over the thread. Resolves to { sid, takeover }. */
  reply: (conversationId, body) => postStaff('/api/staff-reply', { conversation_id: conversationId, body }),
  /** Hand the thread back to the agent. */
  release: (conversationId) => postStaff('/api/staff-release', { conversation_id: conversationId }),
  /**
   * Mark the thread handled OFF-SYSTEM (texted/called from a personal phone). Sends
   * nothing. Takes the thread over, records a [handled-offline] note, resolves its open
   * escalations. Resolves to { takeover, escalations_handled }.
   */
  handledOffline: (conversationId) => postStaff('/api/staff-note', { conversation_id: conversationId }),
  /** Is the agents window's CRM text route (CRM-TEXT-2026-10-16) live? Until it is, Needs a human says "coming soon". */
  textReady: () => crmTextReady(),
  /**
   * The Leads view's Text button + compose box (CRM-TEXT-2026-10-16): text ANY lead by its id, with "Send anyway" for
   * outside 8am-8pm their time. Needs a human's Text opens the same compose box; the Worker reads the number from the lead row and applies
   * STOP, quiet hours, the rate limit and the repeat check. Resolves to { sid, conversation_id }.
   */
  textLead: (leadId, body, sendAnyway = false) => postStaff(CRM_TEXT_PATH, { lead_id: leadId, body, send_anyway: sendAnyway === true }),
  /**
   * EXAMS-SEND-2026-10-16: text a CANDIDATE with no lead record, by id -- the same route; it takes candidate_id once the agents
   * window adds it (the contract is in sites tools/reports/EXAMS-SEND-2026-10-16.md). Until then it answers bad_request.
   */
  textCandidate: (candidateId, body, sendAnyway = false) => postStaff(CRM_TEXT_PATH, { candidate_id: candidateId, body, send_anyway: sendAnyway === true }),
  /**
   * EXAMS-SEND-2026-10-16 / EMAIL-CRM-2026-10-22: the company email (crm-mailer through /api/send-email), from Matt.
   * kind 'exam' | 'onboarding' (a portal link in the body) | 'message' (a subject). Resolves to { sent, messageId };
   * throws with .code (recipient_suppressed = a bounce or complaint). Use lib/email.js, which logs the outcome.
   */
  sendEmail: (kind, to, body, subject) => postStaff('/api/send-email', kind === 'message' ? { kind, to, body, subject } : { kind, to, body }),
};

// The agents window's CRM compose/send route (CRM-TEXT-2026-10-16). Until it is live, the Text button says "coming soon".
// The contract the CRM expects is written in tools/reports/NIGHT-DIRECTSTAFF-2026-10-16.md; if the route lands under
// another path, this constant is the one change.
// CRM-TEXT-2026-10-16 landed as functions/api/staff-text.js (GET answers { ready: true } once its secret and Worker base are set).
export const CRM_TEXT_PATH = '/api/staff-text';
/**
 * Live means: GET <path> answers JSON { ready: true }. Strict on purpose -- an unknown path on Pages answers 200 with
 * the app's own index.html, so a status check alone would call a missing route live.
 */
export async function crmTextReady(fetchFn = (...a) => fetch(...a)) {
  try {
    const res = await fetchFn(CRM_TEXT_PATH, { method: 'GET', headers: { accept: 'application/json' } });
    if (!res.ok || !String(res.headers.get('content-type') || '').includes('application/json')) return false;
    const data = await res.json().catch(() => ({}));
    return data && data.ready === true;
  } catch (_) {
    return false;
  }
}
